import React, { useMemo } from 'react';
import { useStores } from '../../../store';
import { observer } from 'mobx-react-lite';
import { InteractiveDataTable } from '../../../shared/interactive-data-table/interactive-data-table';
import { useIpListColumns } from './ip-list-columns-config';
import { useIpListCrudConfig } from './ip-list-crud-config';
import { BulkPolicyUpdate } from '../../../shared/bulk-options/bulk-policy-update';
import {BulkProtectionOptions, IPDirection} from '../../../../utils/enums';
import { EmptyState } from '../../../shared/empty-state/empty-state';
import { StatusBadge } from '../../../shared/status-badge/status-badge';
import { getIpFiltersDisplay } from '../../../../utils/filters-util';
import { ipListFiltersConfig } from '../../../shared/filters/filter-config/ip-list-filters';
import { AddressLimit } from '../../../shared/address-limit/address-limit';
import { IconType } from '@src/components/shared/custom-icon/custom-icon';
import { IP } from '@src/dtos/version.dto';


const mergeDirections = (ips: IP[]): IP[] => {
    const merged = new Map<string, IP>();

    for (const ip of ips) {
        const key = `${ip.value}|${ip.type}|${ip.ipVersion}|${ip.actionType}`;
        const existing = merged.get(key);

        if (existing) {
            const description = existing.description || ip.description;
            merged.set(key, { ...existing, description, direction: IPDirection.BOTH });
        } else {
            merged.set(key, ip);
        }
    }

    return Array.from(merged.values());
};

export const IpList: React.FC = observer(() => {
    const {fwVersionStore, customerStore} = useStores();
    const { selectedFwVersion, isLoading} = fwVersionStore;

    const mergedIpList = useMemo(
        () => mergeDirections(fwVersionStore.getIpList),
        [fwVersionStore.getIpList]
    );

    const filterConfig = useMemo(() => {
        if(!customerStore.selectedLocation) {
            return ipListFiltersConfig;
        }
        return getIpFiltersDisplay(customerStore.selectedLocation.ipv6Supported, ipListFiltersConfig);
    }, [customerStore.selectedLocation?.ipv6Supported]);

    const statusBadgeAndLimitIcon = (
        <>
            <StatusBadge pageName={BulkProtectionOptions.ALLOW_LIST} />
            <StatusBadge pageName={BulkProtectionOptions.BLOCK_LIST} />
            <AddressLimit />
        </>
    );

    return <div className="allow-list" data-testid="allow-list">
        <InteractiveDataTable filterConfig={filterConfig}
                              data={mergedIpList}
                              columnsConfig={useIpListColumns()}
                              crudConfig={useIpListCrudConfig()}
                              statusBadge={statusBadgeAndLimitIcon}
                              emptyStateComp={<EmptyState
                                  iconType={IconType.EMPTY_LIST}
                                  subTitle="To add new item click"
                                  secondarySubTitle="Add IP Address"
                              />}
                              dataHasLoaded={!!selectedFwVersion}
                              isLoading={isLoading}
                              defaultSortColumn={'value'}
        >
            <BulkPolicyUpdate tab={BulkProtectionOptions.IP_CONTROL}/>
        </InteractiveDataTable>
    </div>;
});